import { DependenciesSection } from '../../DependenciesSection';
import { useDependencies } from '../../hooks/useDependencies';
import { TagsHeader, TagType } from '../../header/TagsHeader';
import { useSelector } from 'react-redux';
import { selectTagsList } from '../../../../../reducers/documentation/userInputsReducer';
import { Box } from '@mui/material';
import { selectAccountUsers } from '../../../../../reducers/accountUsersReducer';
import { parseIdToValueAndNameToLabel, mergeConfigurationMaps } from './utils';
import { useTags } from './useTags';
import { useFilterConfigurations } from './useFilterConfigurations';
import { prepareConfigItemMapStructure } from '../../helpers';
import { SearchProvider } from '../../search/SearchProvider';
import { useMemo, useRef } from 'react';
import { UnclassifiedTypes } from '../../types';

interface TagEditPageProps {
  crmOrgId: string;
  tagId: string;
}

export const TagEditPage = ({ crmOrgId, tagId }: TagEditPageProps) => {
  const {
    tagConfigurations,
    configurationsOwned,
    collaboratorConfigurations,

    collaborationsActiveState,
    configurationsActiveState,
    userConfigurationsActiveState,

    onToggleCollaborationsActiveInactive,
    onToggleConfigurationsActiveInactive,
    onToggleUserConfigurationsActiveInactive,
  } = useFilterConfigurations(tagId);

  const { handleSetConfigurationItem } = useDependencies(crmOrgId);
  const { fetchTagConfigurations, fetchUserConfigurations } = useTags(crmOrgId);

  const tags = useSelector(selectTagsList);
  const users = useSelector(selectAccountUsers);
  const _parsedUsers = parseIdToValueAndNameToLabel(users ?? []);

  const tag = tags?.find((tag) => tag.id === tagId);
  const user = _parsedUsers?.find((user) => user.value === tagId);

  const item = {
    type: tag ? TagType.tag : TagType.user,
    id: tag?.id ?? user?.value ?? '',
    name: tag?.name ?? user?.label ?? '',
    color: tag?.color ?? user?.tagColor,
  };

  const isTag = item.type === TagType.tag;
  const isUser = item.type === TagType.user;

  const containerRef = useRef<HTMLDivElement>(null);

  const tagsConfigurationItems = useMemo(
    () => prepareConfigItemMapStructure(tagConfigurations),
    [tagConfigurations],
  );
  const configurationOwnedItems = useMemo(
    () => prepareConfigItemMapStructure(configurationsOwned),
    [configurationsOwned],
  );
  const collaboratorConfigurationItems = useMemo(
    () => prepareConfigItemMapStructure(collaboratorConfigurations),
    [collaboratorConfigurations],
  );

  const mergedUserConfigurations = useMemo(() => {
    return mergeConfigurationMaps(configurationsOwned, collaboratorConfigurations);
  }, [configurationsOwned, collaboratorConfigurations]);

  const userConfigurationItems = useMemo(
    () => prepareConfigItemMapStructure(mergedUserConfigurations),
    [mergedUserConfigurations],
  );
  const searchProviderConfigItems = isTag ? tagsConfigurationItems : userConfigurationItems;

  return (
    <SearchProvider
      configurationItem={{
        ...item,
        label: item.name,
        parentType: UnclassifiedTypes.tag,
        metadataId: item.id,
        _oldId: item.id,
        filename: '',
      }}
      configurationItems={searchProviderConfigItems}
      configurationsActiveState={isTag ? configurationsActiveState : undefined}
      scrollContainerRef={containerRef}
    >
      <TagsHeader tagType={item.type} tag={item} userEmail={user?.email} crmOrgId={crmOrgId} />

      <Box position="relative" height="100%" overflow="hidden" width="100%">
        <Box
          ref={containerRef}
          p={2}
          gap={2}
          display="flex"
          flexDirection="column"
          overflow="auto"
          height="100%"
        >
          {isTag && (
            <DependenciesSection
              crmOrgId={crmOrgId}
              key={`configurations`}
              id={item.id ?? ''}
              name={item.name ?? ''}
              objectName={'Unknown'}
              configurationItems={tagsConfigurationItems}
              title="Configurations"
              onChooseNewRule={handleSetConfigurationItem}
              onToggleActiveInactive={onToggleConfigurationsActiveInactive}
              activeState={configurationsActiveState}
              fetchConfigurationsApi={fetchTagConfigurations}
              filename={''}
            />
          )}

          {isUser && (
            <DependenciesSection
              crmOrgId={crmOrgId}
              key={`owned`}
              id={item.id ?? ''}
              name={item.name ?? ''}
              objectName={'Unknown'}
              configurationItems={configurationOwnedItems}
              title="Configurations Owned"
              onChooseNewRule={handleSetConfigurationItem}
              onToggleActiveInactive={onToggleUserConfigurationsActiveInactive}
              activeState={userConfigurationsActiveState}
              fetchConfigurationsApi={fetchUserConfigurations}
              filename={''}
            />
          )}

          {isUser && (
            <DependenciesSection
              crmOrgId={crmOrgId}
              key={`collaborations`}
              id={item.id ?? ''}
              name={item.name ?? ''}
              objectName={'Unknown'}
              configurationItems={collaboratorConfigurationItems}
              title="Collaborations"
              onChooseNewRule={handleSetConfigurationItem}
              onToggleActiveInactive={onToggleCollaborationsActiveInactive}
              activeState={collaborationsActiveState}
              filename={''}
            />
          )}
        </Box>
      </Box>
    </SearchProvider>
  );
};
